import React, { useState } from 'react';
import { 
  User, 
  Mail, 
  Phone, 
  Building2, 
  Calendar, 
  Clock, 
  MessageSquare,
  CheckCircle,
  ArrowLeft,
  Loader2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import SaaSHeader from './SaaSHeader';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.theskillgenie.com';

const PublicBookDemo = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    phone_number: '',
    company_name: '',
    state: '',
    district: '',
    preferred_date: '',
    preferred_time: '',
    message: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const res = await fetch(`${API_BASE}/api/crm/public/book-demo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.error || 'Failed to book demo');
      }
      
      setSuccess(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <SaaSHeader />
        <div className="flex-1 flex items-center justify-center p-6 pt-24">
          <div className="max-w-md w-full bg-white rounded-3xl shadow-xl p-8 text-center animate-in fade-in zoom-in duration-300">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Request Received!</h2>
            <p className="text-gray-600 mb-8 text-lg">
              Thank you for your interest in The Skill Genie. Our team will review your request and send a confirmation email with a Google Meet link shortly.
            </p>
            <button
              onClick={() => navigate('/')}
              className="w-full py-4 bg-purple-600 text-white rounded-2xl font-bold hover:bg-purple-700 transition-all shadow-lg shadow-purple-200"
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc] flex flex-col">
      <SaaSHeader />
      
      <div className="flex-1 flex items-center justify-center p-6 pt-32 pb-16">
        <div className="max-w-2xl w-full bg-white rounded-[2.5rem] shadow-2xl shadow-purple-100/50 p-8 md:p-12 relative overflow-hidden">
          {/* Decorative Background Elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-purple-50 rounded-full -mr-32 -mt-32 blur-3xl opacity-50" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-50 rounded-full -ml-32 -mb-32 blur-3xl opacity-50" />
          
          <div className="relative">
            <button 
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-gray-500 hover:text-purple-600 transition-colors mb-8 group"
            >
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
              <span className="text-sm font-semibold">Back</span>
            </button>
            
            <div className="mb-10 text-center md:text-left">
              <h1 className="text-4xl font-black text-gray-900 mb-4 tracking-tight">Book a Demo</h1>
              <p className="text-gray-500 text-lg font-medium">Schedule a personalized ExiHire demo with our team.</p>
            </div>

            {error && (
              <div className="mb-8 p-4 bg-red-50 border border-red-100 rounded-2xl text-red-600 text-sm font-medium flex items-center gap-3">
                <div className="w-2 h-2 bg-red-600 rounded-full" />
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Full Name */}
                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700 ml-1">Full Name *</label>
                  <div className="relative group">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-purple-600 transition-colors" />
                    <input
                      required
                      type="text"
                      name="full_name"
                      placeholder="John Doe"
                      value={form.full_name}
                      onChange={handleChange}
                      className="w-full pl-12 pr-4 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-purple-600 outline-none transition-all text-gray-800 font-medium placeholder:text-gray-400"
                    />
                  </div>
                </div>

                {/* Work Email */}
                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700 ml-1">Work Email *</label>
                  <div className="relative group">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-purple-600 transition-colors" />
                    <input
                      required
                      type="email"
                      name="email"
                      placeholder="john@company.com"
                      value={form.email}
                      onChange={handleChange}
                      className="w-full pl-12 pr-4 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-purple-600 outline-none transition-all text-gray-800 font-medium placeholder:text-gray-400"
                    />
                  </div>
                </div>

                {/* Phone Number */}
                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700 ml-1">Phone Number (Optional)</label>
                  <div className="relative group">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-purple-600 transition-colors" />
                    <input
                      type="tel"
                      name="phone_number"
                      placeholder="+91 98765 43210"
                      value={form.phone_number}
                      onChange={handleChange}
                      className="w-full pl-12 pr-4 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-purple-600 outline-none transition-all text-gray-800 font-medium placeholder:text-gray-400"
                    />
                  </div>
                </div>

                {/* College Name */}
                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700 ml-1">College Name</label>
                  <div className="relative group">
                    <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-purple-600 transition-colors" />
                    <input
                      type="text"
                      name="company_name"
                      placeholder="Enter College Name"
                      value={form.company_name}
                      onChange={handleChange}
                      className="w-full pl-12 pr-4 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-purple-600 outline-none transition-all text-gray-800 font-medium placeholder:text-gray-400"
                    />
                  </div>
                </div>

                {/* State */}
                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700 ml-1">State</label>
                  <div className="relative group">
                    <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-purple-600 transition-colors" />
                    <input
                      type="text"
                      name="state"
                      placeholder="Enter State"
                      value={form.state}
                      onChange={handleChange}
                      className="w-full pl-12 pr-4 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-purple-600 outline-none transition-all text-gray-800 font-medium placeholder:text-gray-400"
                    />
                  </div>
                </div>

                {/* District */}
                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700 ml-1">District</label>
                  <div className="relative group">
                    <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-purple-600 transition-colors" />
                    <input
                      type="text"
                      name="district"
                      placeholder="Enter District"
                      value={form.district}
                      onChange={handleChange}
                      className="w-full pl-12 pr-4 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-purple-600 outline-none transition-all text-gray-800 font-medium placeholder:text-gray-400"
                    />
                  </div>
                </div>

                {/* Preferred Date */}
                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700 ml-1">Preferred Date *</label>
                  <div className="relative group">
                    <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-purple-600 transition-colors" />
                    <input
                      required
                      type="date"
                      name="preferred_date"
                      min={new Date().toISOString().split('T')[0]}
                      value={form.preferred_date}
                      onChange={handleChange}
                      className="w-full pl-12 pr-4 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-purple-600 outline-none transition-all text-gray-800 font-medium placeholder:text-gray-400"
                    />
                  </div>
                </div>

                {/* Preferred Time */}
                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700 ml-1">Preferred Time *</label>
                  <div className="relative group">
                    <Clock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-purple-600 transition-colors" />
                    <input
                      required
                      type="time"
                      name="preferred_time"
                      value={form.preferred_time}
                      onChange={handleChange}
                      className="w-full pl-12 pr-4 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-purple-600 outline-none transition-all text-gray-800 font-medium placeholder:text-gray-400"
                    />
                  </div>
                </div>
              </div>

              {/* Message */}
              <div className="space-y-2">
                <label className="text-sm font-bold text-gray-700 ml-1">Message (Optional)</label>
                <div className="relative group">
                  <MessageSquare className="absolute left-4 top-5 w-5 h-5 text-gray-400 group-focus-within:text-purple-600 transition-colors" />
                  <textarea
                    name="message"
                    rows="4"
                    placeholder="Tell us about your requirements..."
                    value={form.message}
                    onChange={handleChange}
                    className="w-full pl-12 pr-4 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-purple-600 outline-none transition-all text-gray-800 font-medium placeholder:text-gray-400 resize-none"
                  />
                </div>
              </div>

              <button
                disabled={loading}
                type="submit"
                className="w-full py-5 bg-purple-600 text-white rounded-[1.25rem] font-black text-lg hover:bg-purple-700 active:scale-[0.98] transition-all shadow-xl shadow-purple-200 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-3"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-6 h-6 animate-spin" />
                    Scheduling...
                  </>
                ) : (
                  'Schedule Demo'
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicBookDemo;
